import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '#root/lib/utils';
import styles from './input.module.scss';

/**
 * Variantes de input usando class-variance-authority + SCSS modules
 */
const inputVariants = cva(styles.field, {
  variants: {
    size: {
      sm: styles.sm,
      md: styles.md,
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  /**
   * Etiqueta flotante. Sube sobre el borde cuando hay valor o foco.
   */
  label?: React.ReactNode;
  /**
   * Marca el campo como inválido (borde y texto de ayuda en rojo)
   */
  error?: boolean;
  /**
   * Texto de ayuda debajo del campo
   */
  helperText?: React.ReactNode;
  /**
   * Contenido fijo al inicio del campo (ej: un icono de moneda)
   * @example <Input startAdornment={<DollarSignIcon />} />
   */
  startAdornment?: React.ReactNode;
  /**
   * Contenido fijo al final del campo
   */
  endAdornment?: React.ReactNode;
  /**
   * Renderiza un textarea en vez de un input
   */
  multiline?: boolean;
  /**
   * Filas visibles cuando `multiline` es true
   */
  rows?: number;
  /**
   * Ocupa todo el ancho disponible
   */
  fullWidth?: boolean;
  /**
   * Clase para el contenedor externo (el campo + su texto de ayuda)
   */
  wrapperClassName?: string;
}

/**
 * Input Component - Design System
 *
 * Campo de texto con etiqueta flotante, independiente de Material-UI.
 *
 * A diferencia del `TextField` de MUI v4, el estado de la etiqueta se deriva del
 * render y no de un `useRef` fijado en el primer montaje: un `value` que llega
 * de forma asíncrona (un fetch que resuelve después) hace flotar la etiqueta
 * correctamente. El `value` se normaliza a cadena vacía, así que el campo nunca
 * pasa de controlado a no controlado.
 *
 * @example
 * <Input id="city" label="Ciudad" value={city} onChange={handleChange} />
 *
 * @example
 * // Con adorno inicial y error
 * <Input
 *   id="price"
 *   label="Precio"
 *   startAdornment={<DollarSignIcon />}
 *   error={hasError}
 *   helperText="Debe ser mayor a 0"
 * />
 *
 * @example
 * // Área de texto
 * <Input id="message" label="Mensaje" multiline rows={4} />
 */
const Input = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  (
    {
      className,
      wrapperClassName,
      size,
      label,
      error = false,
      helperText,
      startAdornment,
      endAdornment,
      multiline = false,
      rows = 4,
      fullWidth = true,
      disabled = false,
      value,
      id,
      onFocus,
      onBlur,
      ...props
    },
    ref
  ) => {
    const [focused, setFocused] = React.useState(false);

    // Nunca `undefined`: el campo es controlado desde el primer render.
    const safeValue = value ?? '';
    const hasValue = String(safeValue).length > 0;
    const isFloating = focused || hasValue || Boolean(startAdornment) || Boolean(props.placeholder);

    const helperId = helperText && id ? `${id}-helper` : undefined;

    const handleFocus = (event: any) => {
      setFocused(true);
      onFocus?.(event);
    };

    const handleBlur = (event: any) => {
      setFocused(false);
      onBlur?.(event);
    };

    const controlProps = {
      id,
      value: safeValue,
      disabled,
      className: cn(styles.control, className),
      onFocus: handleFocus,
      onBlur: handleBlur,
      'aria-invalid': error || undefined,
      'aria-describedby': helperId,
      ...props,
    };

    return (
      <div
        className={cn(
          styles.root,
          fullWidth && styles.fullWidth,
          disabled && styles.disabled,
          error && styles.error,
          wrapperClassName
        )}>
        <div
          className={cn(
            inputVariants({ size }),
            isFloating && styles.floating,
            focused && styles.focused,
            multiline && styles.multiline
          )}>
          {startAdornment && <span className={styles.adornment}>{startAdornment}</span>}

          {multiline ? (
            <textarea
              {...(controlProps as any)}
              rows={rows}
              ref={ref as React.Ref<HTMLTextAreaElement>}
            />
          ) : (
            <input {...(controlProps as any)} ref={ref as React.Ref<HTMLInputElement>} />
          )}

          {endAdornment && <span className={styles.adornment}>{endAdornment}</span>}

          {label && (
            <label className={styles.label} htmlFor={id}>
              {label}
            </label>
          )}

          {/* La muesca del borde: el legend recorta el trazo donde va la etiqueta,
              así funciona sobre cualquier fondo. */}
          <fieldset className={styles.outline} aria-hidden="true">
            {label && (
              <legend className={styles.legend}>
                <span>{label}</span>
              </legend>
            )}
          </fieldset>
        </div>

        {/* <div> y no <p>: `helperText` es ReactNode y un caller con contenido
            en bloque produciria un <p> anidado dentro de otro. */}
        {helperText && (
          <div className={styles.helper} id={helperId}>
            {helperText}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input, inputVariants };
